import {
  addDays,
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { useMemo } from 'react';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getTaskPlanning } from 'shared/utils/get-task-planning';
import {
  getScheduleByDate,
  getScheduleTimeframeByDate,
} from 'shared/utils/schedules';

export type Options = {
  weekStartsOn: WeekDays;
  today: Timestamp;
};

export const useTaskPlannedOccurrenceForDate = (
  task: Task,
  date: Timestamp,
  { weekStartsOn, today }: Options,
) =>
  useMemo(() => {
    const planning = getTaskPlanning(task, {
      startDate: startOfDay(date),
      endDate: endOfDay(date),
      weekStartsOn,
    });
    const plannedOnDate = planning.find(({ date: _date }) =>
      isSameDay(_date, date),
    );

    if (plannedOnDate) {
      return plannedOnDate;
    }

    // if we display a date in the past, display the first past occurrence
    if (isSameDay(date, today) || isBefore(date, today)) {
      const activeSchedule = getScheduleByDate(task.schedules, date);
      const currentTimeframe = activeSchedule
        ? getScheduleTimeframeByDate(activeSchedule, date, weekStartsOn)
        : undefined;
      // get the previous timeframe based on the current timeframe, as there could be an overdue entry there that we have to display
      const prevTimeframe =
        activeSchedule && currentTimeframe
          ? getScheduleTimeframeByDate(
              activeSchedule,
              addDays(currentTimeframe.startDate, -1),
              weekStartsOn,
            )
          : undefined;
      const overduePlanning = getTaskPlanning(task, {
        startDate: prevTimeframe?.startDate ?? startOfDay(today),
        endDate: endOfDay(today),
        weekStartsOn,
        strictEndDate: true,
      });

      // get the last valid entry date
      const lastEntryDate = activeSchedule?.entries?.reduce<
        Timestamp | undefined
      >((acc, entry) => {
        if (entry.state === ScheduleEntryState.Complete && !entry.value) {
          return acc;
        }
        if (!acc) {
          return entry.date;
        }

        return isAfter(entry.date, acc) ? entry.date : acc;
      }, undefined);

      // filter the planning to only include the overdue entries since the last valid entry date
      const filteredOverduePlanning = lastEntryDate
        ? overduePlanning.filter(({ date }) => isAfter(date, lastEntryDate))
        : overduePlanning;

      return filteredOverduePlanning[filteredOverduePlanning.length - 1];
    }

    // return the first occurrence after the display date
    return planning.find(({ date: _date }) => isAfter(_date, date));
  }, [date, task, today, weekStartsOn]);
